import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import styled from "styled-components";
import Table from "../../components/core-components/Table.tsx";
import PageHeader from "../../components/PageHeader.tsx";
import Scrollbars from "react-custom-scrollbars-2";
import { getOrders } from "../../../services/orderService.js";
import InputField from "../../components/core-components/InputField.tsx";
import { useNavigate } from "react-router-dom";
import { getStatusColor } from "../../../utils/utils.ts";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  gap: 1rem;
  width: 100%;
  height: 100%;
`;
const Content = styled.div`
  box-sizing: border-box;
  background-color: white;
  width: calc(100% - 2rem);
  min-height: 80%;
  border-radius: 8px;
  padding: 20px;
  overflow-x: auto; /* Enable horizontal scroll */
  overflow-y: auto; /* Optional: keep vertical scroll */

  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
`;
const SortHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  box-sizing: border-box;
  background-color: white;
  width: calc(100% - 2rem);

  border-radius: 8px;
  padding: 20px;

  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);

  & > div {
    flex: 1;
  }

  @media (max-width: 820px) {
    flex-direction: column;
  }
`;

const Sort = styled.div`
  margin-right: 1rem;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
  color: var(--tertiary-font-color);
  font-family: Georgia, serif;

  @media (min-width: 820px) {
    margin-left: 2rem;
  }

  select {
    padding: 4px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 5px;
    width: 100%;
  }

  div {
    border: 0.5px solid rgba(0, 0, 0, 0.1);
    border-radius: 5px;
  }

  select:focus {
    outline: none;
  }

  label {
    width: 30%;
  }
`;

const Status = styled.div<{
  $color: string;
  $backgroundColor?: string;
}>`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  color: ${({ $color }) => $color};
  padding: 4px;
  border-radius: 5px;
  text-align: center;
`;

const DashboardOrdersPage: React.FC = () => {
  const [statusSort, setStatusSort] = useState("None");
  const [dateSort, setDateSort] = useState("");
  const [phone, setPhone] = useState("");

  const navigate = useNavigate();

  const { data: orders = [] } = useQuery({
    queryKey: ["orders"],
    queryFn: () => getOrders(),
  });

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusSort(event.target.value);
  };
  const filteredOrders = orders
    .filter((order) => {
      const matchesStatus =
        statusSort === "None" || order.status === statusSort;

      const matchesDate =
        !dateSort ||
        (() => {
          const [day, month, year] = order.orderDate.split("/");
          const formattedOrderDate = `${year}-${month.padStart(
            2,
            "0"
          )}-${day.padStart(2, "0")}`;
          return formattedOrderDate === dateSort;
        })();

      const matchesPhone = !phone || order.phoneNumber?.includes(phone);

      return matchesStatus && matchesDate && matchesPhone;
    })
    .reverse();

  return (
    <Wrapper>
      <PageHeader pageTitle="Comenzi" />
      <SortHeader>
        <Sort>
          <label htmlFor="sort">Status:</label>
          <select
            name="sort"
            id="sort"
            value={statusSort}
            onChange={handleSortChange}
          >
            <option value="None">Toate</option>
            <option value="Nou">Nou</option>
            <option value="In prelucrare">In prelucrare</option>
            <option value="Livrat">Livrat</option>
            <option value="Anulat">Anulat</option>
          </select>
        </Sort>
        <Sort>
          <label>Data comandă:</label>
          <InputField
            name="orderDate"
            type="date"
            value={dateSort}
            onChange={(e) => setDateSort(e.target.value)}
            placeholder={"Data comandă"}
          />
        </Sort>
        <Sort>
          <label>Telefon client:</label>
          <InputField
            name="phone"
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={"Telefon"}
          />
        </Sort>
      </SortHeader>
      <Content>
        <Scrollbars style={{}}>
          <Table
            columns={[
              { title: "ID", key: "orderID" },
              { title: "Data", key: "orderDate" },
              { title: "Status", key: "status" },
              { title: "Status plata", key: "paymentStatus" },
              { title: "Suma totală", key: "totalPrice" },
              { title: "Nume", key: "name" },
              { title: "Telefon", key: "phoneNumber" },
            ]}
            rows={filteredOrders.map((order) => ({
              ...order,
              totalPrice: `${order.totalPrice} lei`,
              name: `${order.firstName} ${order.lastName}`,
              status: (
                <Status
                  $backgroundColor={
                    getStatusColor(order.status).backgroundColor
                  }
                  $color={getStatusColor(order.status).color}
                >
                  {order.status}
                </Status>
              ),
              paymentStatus: (
                <Status
                  $backgroundColor={
                    getStatusColor(order.paymentStatus).backgroundColor
                  }
                  $color={getStatusColor(order.paymentStatus).color}
                >
                  {order.paymentStatus}
                </Status>
              ),
            }))}
            onRowClick={(row) => {
              row?.orderID && navigate(row?.orderID.toString());
            }}
          />
        </Scrollbars>
      </Content>
    </Wrapper>
  );
};

export default DashboardOrdersPage;
